// The only module that imports `posthog-js` directly, and the only access
// path for code that is not a React component. Components use
// `useAnalyticsClient()` from `./react.jsx`.
//
// The SDK is imported lazily inside `initAnalytics()` so Node-environment
// tests that reach this module through restClient or analyticsClient never
// load it (it touches `window` at import time). Disabled builds never load
// it either and hand every caller `noopClient`.
//
// The event registry, events.js, is imported beside it for another reason:
// ad blockers block its dev URL, and only a failed import() can be caught.
// Event names and values come from surfaceVocabulary.js, loaded at startup.
import {
    chatPersistenceEnabled,
    comparisonsEnabled,
    inventorySurfacesEnabled,
    productAnalyticsEnabled,
    securityTabEnabled,
    skillsFeatureEnabled,
} from "../featureFlags.js";
import { defineEnum } from "./enum.js";
import { EVENT_NAME, EVENT_RATE_LIMITS, EXCEPTION_SOURCE, GATE } from "./surfaceVocabulary.js";
import { CREDENTIAL_PARAMS, buildBeforeSend } from "./sanitize.js";
import { ANALYTICS_REGION } from "./tenantGate.js";

const noop = () => undefined;

// Everything this layer and the React bindings call. Before `init`, the real
// SDK logs an error on every call, so the uninitialized instance must never
// reach a caller; this stands in for it.
export const noopClient = Object.freeze({
    __loaded: false,
    capture: noop,
    identify: noop,
    group: noop,
    register: noop,
    unregister: noop,
    reset: noop,
    captureException: noop,
    isFeatureEnabled: () => undefined,
    getFeatureFlag: () => undefined,
    getFeatureFlagPayload: () => undefined,
    onFeatureFlags: () => noop,
    get_distinct_id: () => "",
    opt_in_capturing: noop,
    opt_out_capturing: noop,
});

let _client = null;
let _enabled = false;
// Whether this page load started the SDK, so a paused client can resume.
let _started = false;
// The region whose project the SDK was started for, null until it starts.
let _region = null;
let _listenersInstalled = false;
let _unloading = false;

// Fetches that a navigation tears down reject after `pagehide`; a failure
// reported then is the person leaving, not the backend failing.
export function isPageUnloading() {
    return _unloading;
}

if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
    window.addEventListener("pagehide", () => { _unloading = true; });
    window.addEventListener("pageshow", () => { _unloading = false; });
}

export function isAnalyticsEnabled() {
    return _enabled;
}

export function getClient() {
    return _client ?? noopClient;
}

// What the rate limiter decides for one event: send it, drop it but send
// the one burst marker for its window, or drop it. The caps live beside the
// registry in events.js as EVENT_RATE_LIMITS.
export const ADMISSION = defineEnum("ADMISSION", {
    SEND: "send",
    MARKER: "marker",
    DROP: "drop",
});

export function createRateLimiter(limits = EVENT_RATE_LIMITS, now = () => Date.now()) {
    const windows = new Map();
    return {
        admit(event) {
            const rule = limits[event];
            if (!rule) return ADMISSION.SEND;
            const t = now();
            let w = windows.get(event);
            if (!w || t - w.start >= rule.windowMs) {
                w = { start: t, count: 0, marked: false };
                windows.set(event, w);
            }
            w.count += 1;
            if (w.count <= rule.limit) return ADMISSION.SEND;
            if (w.marked) return ADMISSION.DROP;
            w.marked = true;
            return ADMISSION.MARKER;
        },
        reset() {
            windows.clear();
        },
    };
}

let _limiter = createRateLimiter();

function admit(client, event) {
    const verdict = _limiter.admit(event);
    if (verdict === ADMISSION.MARKER) {
        const rule = EVENT_RATE_LIMITS[event];
        client.capture(EVENT_NAME.ANALYTICS_THROTTLED, { throttled_event: event, limit: rule.limit, window_ms: rule.windowMs });
    }
    return verdict === ADMISSION.SEND;
}

export function track(event, properties) {
    const client = getClient();
    if (admit(client, event)) client.capture(event, properties);
}

// Every exception path, the facade below, the window listeners and the React
// root handlers, goes through here so one loop cannot flood the project.
export function reportException(client, error, properties) {
    if (!admit(client, EVENT_NAME.EXCEPTION)) return undefined;
    return client.captureException(error, properties);
}

export function captureException(error, properties) {
    return reportException(getClient(), error, properties);
}

// The SDK's own unhandled-error hooks are an external script in the default
// bundle and external loading is disabled, so the two listeners live here.
export function installWindowListeners(client, target = typeof window !== "undefined" ? window : null) {
    if (!target || _listenersInstalled) return;
    _listenersInstalled = true;
    target.addEventListener("error", (event) => {
        const error = event?.error instanceof Error ? event.error : new Error(String(event?.message ?? "Unknown error"));
        reportException(client, error, { $exception_source: EXCEPTION_SOURCE.WINDOW_ERROR });
    });
    target.addEventListener("unhandledrejection", (event) => {
        const reason = event?.reason;
        const error = reason instanceof Error ? reason : new Error(typeof reason === "string" ? reason : "Unhandled rejection");
        reportException(client, error, { $exception_source: EXCEPTION_SOURCE.UNHANDLED_REJECTION });
    });
}

// Hosts a developer's machine serves the dashboard from. Development builds
// are refused by build type already; this also covers a production build
// served locally, such as `vite preview`, whatever a local env file supplies.
const LOCAL_HOSTNAME = /^(?:localhost|0\.0\.0\.0|127(?:\.\d{1,3}){3}|\[::1\]|::1)$|\.localhost$/i;

export function isLocalHostname(hostname) {
    return typeof hostname === "string" && LOCAL_HOSTNAME.test(hostname);
}

// Why a page must not start the SDK.
export const BLOCKED_BY = defineEnum("BLOCKED_BY", {
    DEVELOPMENT_BUILD: "development build",
    LOCAL_HOST: "local host",
    CREDENTIALS_IN_URL: "an OAuth code or a token in the page URL",
    NOT_ENABLED: "not enabled for this environment",
    NO_KEY_OR_HOST: "no PostHog key or host in this build",
    NO_TENANT_REGION: "this tenant has no product analytics region, or its setting could not be read",
    REGION_NOT_CONFIGURED: "no PostHog key or host in this build for the tenant's region",
});

function carriesCredentials(search, hash) {
    const lists = [new URLSearchParams(search || ""), new URLSearchParams(String(hash || "").replace(/^#/, ""))];
    return lists.some((params) => CREDENTIAL_PARAMS.some((name) => params.has(name)));
}

// Only deployed environments send, and only when their build configuration
// enables it. Returns a BLOCKED_BY member, or null when the SDK may start.
// The SDK keeps the first URL it sees in the browser's storage, so it must
// never start on a page whose URL still carries an OAuth code or a token:
// main.jsx starts it only after auth bootstrap has stripped them, and this
// keeps analytics off for the page load if something did not.
export function analyticsBlocker({ enabled, key, host, dev, hostname, search = "", hash = "" }) {
    if (dev) return BLOCKED_BY.DEVELOPMENT_BUILD;
    if (isLocalHostname(hostname)) return BLOCKED_BY.LOCAL_HOST;
    if (carriesCredentials(search, hash)) return BLOCKED_BY.CREDENTIALS_IN_URL;
    if (!enabled) return BLOCKED_BY.NOT_ENABLED;
    if (!key || !host) return BLOCKED_BY.NO_KEY_OR_HOST;
    return null;
}

function trimmed(value) {
    return typeof value === "string" ? value.trim() : "";
}

// One PostHog project per region; the US one keeps the original variable
// names. A region whose key or host is missing cannot start.
function readEnv() {
    const env = (typeof import.meta !== "undefined" && import.meta.env) || {};
    return {
        projects: {
            [ANALYTICS_REGION.US]: { key: trimmed(env.VITE_POSTHOG_KEY), host: trimmed(env.VITE_POSTHOG_HOST) },
            [ANALYTICS_REGION.EU]: { key: trimmed(env.VITE_POSTHOG_EU_KEY), host: trimmed(env.VITE_POSTHOG_EU_HOST) },
        },
        dev: Boolean(env.DEV),
        toolbar: env.VITE_POSTHOG_TOOLBAR === "1",
    };
}

function enabledGates() {
    const gates = [];
    if (securityTabEnabled()) gates.push(GATE.SECURITY_TAB);
    if (comparisonsEnabled()) gates.push(GATE.COMPARISONS);
    if (inventorySurfacesEnabled()) gates.push(GATE.INVENTORY);
    if (skillsFeatureEnabled()) gates.push(GATE.SKILLS);
    if (chatPersistenceEnabled()) gates.push(GATE.CHAT_PERSISTENCE);
    return gates;
}

function buildEnv() {
    return (typeof import.meta !== "undefined" && import.meta.env) || {};
}

function envString(value) {
    return typeof value === "string" && value ? value : "unknown";
}

// Super properties fixed for the page load: the build's environment, version
// and revision (vite.config.js), and the gates this browser has on. The SDK's `loaded` hook registers them before
// the page's first pageview, and PostHogIdentity registers them again after
// a reset clears them, so no event waits on the user store.
export function buildProperties() {
    const env = buildEnv();
    return {
        app_env: envString(env.MODE),
        app_version: envString(env.VITE_APP_VERSION),
        app_revision: envString(env.VITE_APP_REVISION),
        gates: enabledGates(),
    };
}

// The first before_send step: drops every event while this tab's gate is
// closed. It runs on everything the SDK sends through capture, explicit
// events and SDK-generated ones alike ($pageview, autocapture, $identify,
// heatmaps). It deliberately does not use the SDK's opt-out: `reset()`
// clears that, and the SDK keeps it in storage every tab of this project
// shares, while the dashboard's sign-in, and so the gate, is per tab.
export function tabGateStep(isOpen) {
    return (event) => (isOpen() === true ? event : null);
}

// `isOpen` is the tab's gate and `enforceRegistry` the registry's last step;
// without either, nothing is sent.
export function sdkConfig({ host, toolbar = false, isOpen = () => false, enforceRegistry }) {
    return {
        api_host: host,
        defaults: "2026-08-30",
        person_profiles: "identified_only",
        // Nothing is fetched from PostHog's CDN: recorder, surveys, web
        // vitals and the exception hooks never load, which also satisfies
        // the prod CSP. The one exception is an environment that opts into
        // the PostHog toolbar (heatmaps), which is a CDN script loaded on
        // demand; recording, surveys, dead clicks and the exception hooks
        // stay disabled by the settings below even there.
        disable_external_dependency_loading: !toolbar,
        autocapture: {
            dom_event_allowlist: ["click", "submit", "change"],
            element_allowlist: ["a", "button", "form", "input", "select", "textarea", "label"],
        },
        mask_all_text: true,
        mask_all_element_attributes: true,
        // This app navigates by query string on `/`.
        capture_pageview: { path: true, search: true },
        capture_pageleave: true,
        // Replaced by installWindowListeners and the React root handlers.
        capture_exceptions: false,
        disable_session_recording: true,
        disable_surveys: true,
        // Heatmap capture is in the bundle, records pointer positions and
        // scroll depth against the URL, never text, and is not billed.
        // Viewing needs the toolbar or the PostHog heatmap page embedding
        // the dashboard; see the dossier.
        capture_heatmaps: true,
        // Dead-click detection is an external script the SDK would fetch
        // from PostHog's CDN, which is disabled above, so it stays off.
        capture_dead_clicks: false,
        respect_dnt: true,
        // Runs before the SDK queues the initial pageview.
        loaded: (instance) => instance.register(buildProperties()),
        before_send: [tabGateStep(isOpen), ...buildBeforeSend({ enforceRegistry })],
    };
}

// Resolves to the client `main.jsx` hands to the provider: the SDK started
// for the tenant's region when the gate is on and the build carries that
// region's key and host, otherwise noopClient. `tenantRegion` resolves to the
// tenant's ANALYTICS_REGION, or null when it has none (see tenantGate.js);
// without it analytics stays off.
export async function initAnalytics({ tenantRegion = async () => null } = {}) {
    if (_client) return _client;
    const { projects, dev, toolbar } = readEnv();
    const location = typeof window !== "undefined" ? window.location : undefined;
    const anyProject = Object.values(projects).find((p) => p.key && p.host) ?? { key: "", host: "" };
    if (analyticsBlocker({
        enabled: productAnalyticsEnabled(),
        key: anyProject.key,
        host: anyProject.host,
        dev,
        hostname: location?.hostname,
        search: location?.search,
        hash: location?.hash,
    })) {
        if (dev) console.info("[analytics] off: local development builds never send to PostHog");
        _client = noopClient;
        return _client;
    }
    // The tenant's region and the registry are read beside the SDK import, so
    // startup waits on the slowest rather than each in turn. The SDK sends
    // nothing until init, which runs only once the region is known.
    const [region, sdk, registry] = await Promise.all([
        Promise.resolve().then(tenantRegion).then((value) => value, () => null),
        import("posthog-js").then((module) => ({ module }), (error) => ({ error })),
        import("./events.js").then((module) => ({ module }), (error) => ({ error })),
    ]);
    const project = Object.hasOwn(projects, region ?? "") ? projects[region] : null;
    if (!project) {
        console.info(`[analytics] off: ${BLOCKED_BY.NO_TENANT_REGION}`);
        _client = noopClient;
        return _client;
    }
    if (!project.key || !project.host) {
        console.info(`[analytics] off: ${BLOCKED_BY.REGION_NOT_CONFIGURED}`);
        _client = noopClient;
        return _client;
    }
    // The dashboard mounts only once this resolves, so a failure here must
    // leave analytics off for the page load rather than reject.
    // The gate opens before init so the page's first pageview, which the SDK
    // queues during init, passes it.
    let posthog;
    _region = region;
    _enabled = true;
    try {
        if (sdk.error) throw sdk.error;
        // Nothing may leave without the registry, so one that did not load
        // leaves analytics off, like an SDK that did not.
        if (registry.error) throw registry.error;
        posthog = sdk.module.default;
        posthog.init(project.key, sdkConfig({
            host: project.host,
            toolbar,
            isOpen: isAnalyticsEnabled,
            enforceRegistry: registry.module.createRegistryEnforcer({ dev }),
        }));
    } catch (error) {
        console.warn("[analytics] off: PostHog failed to start", error);
        _enabled = false;
        _region = null;
        _client = noopClient;
        return _client;
    }
    try {
        installWindowListeners(posthog);
    } catch (error) {
        console.warn("[analytics] window error listeners were not installed", error);
    }
    _client = posthog;
    _started = true;
    return _client;
}

// The region this page load's SDK sends to, or null when it never started.
export function startedRegion() {
    return _started ? _region : null;
}

// Closes this tab's gate the moment the signed-in identity changes, before
// the new tenant's region is known, so a tenant without analytics, or in
// another region, sends nothing in between, the SDK's own reset included.
export function pauseAnalytics() {
    _enabled = false;
}

// Reopens the gate for a new identity whose tenant region is `region`. Only
// a client this page load started can resume, and only into the region it
// started for: the SDK cannot move to another project without a reload.
// Returns whether the gate is open.
export function resumeAnalyticsFor(region) {
    if (!_started || !_client || region === null || region !== _region) return false;
    _enabled = true;
    return true;
}

// Tests inject a recording client and flip the enabled flag without loading
// the SDK.
export function _setClientForTests(client) {
    _client = client;
    _enabled = Boolean(client) && client !== noopClient;
    _started = _enabled;
    _region = _enabled ? ANALYTICS_REGION.US : null;
    _listenersInstalled = false;
    _limiter.reset();
}

export function _resetForTests() {
    _client = null;
    _enabled = false;
    _started = false;
    _region = null;
    _listenersInstalled = false;
    _limiter.reset();
}
